import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { PollAPI } from '../../../../shared/types/api';

// ============================================================================
// USER POLL HOOKS
// ============================================================================

/**
 * Hook to fetch polls available to the current user
 */
export function usePolls() {
  return useQuery({
    queryKey: ['user-polls'],
    queryFn: async (): Promise<PollAPI.GetUserPollsResponse> => {
      return api.get('/user/polls');
    },
  });
}

/**
 * Hook to fetch a single poll for the current user
 */
export function usePoll(pollId: string | undefined) {
  return useQuery({
    queryKey: ['user-poll', pollId],
    queryFn: async (): Promise<PollAPI.GetUserPollResponse> => {
      if (!pollId) throw new Error('Poll ID is required');
      return api.get(`/user/polls/${pollId}`);
    },
    enabled: !!pollId,
  });
}

/**
 * Hook to submit a poll response
 */
export function useSubmitPollResponse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      pollId, 
      answerId 
    }: { 
      pollId: string; 
      answerId: string;
    }): Promise<PollAPI.SubmitPollResponseResponse> => {
      return api.post(`/user/polls/${pollId}/respond`, { answerId });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-polls'] });
      queryClient.invalidateQueries({ queryKey: ['user-poll', variables.pollId] });
      // Club polls are rendered from the club page query.
      queryClient.invalidateQueries({ queryKey: ['clubs'] });
    },
  });
}

// ============================================================================
// ADMIN POLL HOOKS
// ============================================================================

/**
 * Hook to fetch all polls (admin only)
 */
export function useAdminPolls() {
  return useQuery({
    queryKey: ['admin-polls'],
    queryFn: async (): Promise<PollAPI.GetPollsResponse> => {
      return api.get('/admin/polls');
    },
  });
}

/**
 * Hook to fetch a single poll with results (admin only)
 */
export function useAdminPoll(pollId: string | undefined) {
  return useQuery({
    queryKey: ['admin-poll', pollId],
    queryFn: async (): Promise<PollAPI.GetPollResponse> => {
      if (!pollId) throw new Error('Poll ID is required');
      return api.get(`/admin/polls/${pollId}`);
    },
    enabled: !!pollId,
  });
}

/**
 * Hook to create a new poll (admin only)
 */
export function useCreatePoll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: PollAPI.CreatePollRequest): Promise<PollAPI.CreatePollResponse> => {
      return api.post('/admin/polls', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-polls'] });
    },
  });
}

/**
 * Hook to update a poll (admin only)
 */
export function useUpdatePoll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      id, 
      data 
    }: { 
      id: string; 
      data: PollAPI.UpdatePollRequest;
    }): Promise<PollAPI.UpdatePollResponse> => {
      return api.patch(`/admin/polls/${id}`, data);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-polls'] });
      queryClient.invalidateQueries({ queryKey: ['admin-poll', variables.id] });
    },
  });
}

/**
 * Hook to delete a poll (admin only)
 */
export function useDeletePoll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pollId: string): Promise<PollAPI.DeletePollResponse> => {
      return api.delete(`/admin/polls/${pollId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-polls'] });
    },
  });
}

/**
 * Hook to preview target user count (admin only)
 */
export function usePreviewPollTarget() {
  return useMutation({
    mutationFn: async (criteria: PollAPI.PreviewTargetRequest): Promise<PollAPI.PreviewTargetResponse> => {
      return api.post('/admin/polls/preview-target', criteria);
    },
  });
}

